import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  TextField,
  MenuItem,
  Alert,
  Paper,
} from "@mui/material";
import {
  getAdminQuestionnaireById,
  getAdminQuestionnaires,
  updateAdminQuestionnaire,
} from "../../services/questionnaireService";
import { useResponseError } from "../../hooks/useResponseError";
import { ErrorBox } from "../../elements/ErrorBox";
import { InputText } from "../../elements/InputText";

type QuestionnaireListItem = {
  id: string;
  categoryId: string | null;
  categoryName: string;
  questionCount: number;
  updatedAt: string;
};

type QuestionField = {
  id: string;
  question: string;
  options?: { id: string; text: string }[];
  isLabel?: boolean;
  visibleWhenOptionIdIsActive?: string;
  [key: string]: any;
};

const AdminQuestionnairesList = () => {
  const [list, setList] = useState<QuestionnaireListItem[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [fields, setFields] = useState<QuestionField[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const { responseError, setResponseError } = useResponseError(15000);

  useEffect(() => {
    const loadList = async () => {
      setIsLoading(true);
      try {
        const res = await getAdminQuestionnaires();
        setList(res || []);
        if (res?.length) {
          setSelectedId(res[0].id);
        }
      } catch (error: unknown) {
        setResponseError((error as Error).message);
      } finally {
        setIsLoading(false);
      }
    };
    loadList();
  }, [setResponseError]);

  useEffect(() => {
    if (!selectedId) {
      setFields([]);
      return;
    }
    const loadQuestionnaire = async () => {
      setIsLoading(true);
      setSuccessMessage("");
      try {
        const res = await getAdminQuestionnaireById({ id: selectedId });
        const parsed = res?.content ? JSON.parse(res.content) : [];
        setFields(Array.isArray(parsed) ? parsed : []);
      } catch (error: unknown) {
        setResponseError((error as Error).message);
      } finally {
        setIsLoading(false);
      }
    };
    loadQuestionnaire();
  }, [selectedId, setResponseError]);

  const updateQuestionText = (index: number, value: string) => {
    setFields((prev) =>
      prev.map((field, i) => (i === index ? { ...field, question: value } : field))
    );
  };

  const updateOptionText = (fieldIndex: number, optionIndex: number, value: string) => {
    setFields((prev) =>
      prev.map((field, i) => {
        if (i !== fieldIndex || !field.options) {
          return field;
        }
        return {
          ...field,
          options: field.options.map((option, oi) =>
            oi === optionIndex ? { ...option, text: value } : option
          ),
        };
      })
    );
  };

  const handleSave = async () => {
    if (!selectedId) {
      return;
    }
    setIsSaving(true);
    setSuccessMessage("");
    try {
      await updateAdminQuestionnaire({
        id: selectedId,
        content: fields,
      });
      setSuccessMessage("Questionnaire saved.");
      const res = await getAdminQuestionnaires();
      setList(res || []);
    } catch (error: unknown) {
      setResponseError((error as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h1" component="h1" sx={{ mb: "32px" }}>
        Questionnaires
      </Typography>
      {responseError && <ErrorBox sx={{ mb: 2 }}>{responseError}</ErrorBox>}
      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}
      {isLoading && !fields.length ? (
        <Box
          sx={{
            mt: "40px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <TextField
            select
            fullWidth
            label="Questionnaire"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            sx={{ mb: 3, maxWidth: 480 }}
          >
            {list.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.categoryName} ({item.questionCount} questions)
              </MenuItem>
            ))}
          </TextField>

          <Paper
            elevation={0}
            sx={{
              border: "1px solid #DDDFDD",
              p: 3,
              maxWidth: 720,
            }}
          >
            {fields.map((field, index) => (
              <Box key={field.id || index} sx={{ mb: 2 }}>
                <InputText
                  label={`Question ${index + 1}${field.isLabel ? " (label)" : ""}`}
                  value={field.question || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateQuestionText(index, e.target.value)
                  }
                  multiline
                  rows={2}
                  fullWidth
                />
                {field.options?.map((option, optionIndex) => (
                  <InputText
                    key={option.id || optionIndex}
                    label={`Choice ${optionIndex + 1}`}
                    value={option.text || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateOptionText(index, optionIndex, e.target.value)
                    }
                    fullWidth
                  />
                ))}
              </Box>
            ))}
            {!fields.length && (
              <Typography sx={{ color: "#455445" }}>
                No questions found for this questionnaire.
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving || !fields.length}
              sx={{ mt: 2 }}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default AdminQuestionnairesList;
